import React, { useState, useMemo } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    LinearProgress,
    Grid,
    Avatar,
    Chip,
    Paper,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    Stack
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GroupIcon from '@mui/icons-material/Group';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import SchoolIcon from '@mui/icons-material/School';

const MemberCard = ({ 
    member, 
    onViewDetails, 
    onAddToGroup, 
    onRemoveFromGroup,
    onDelete, 
    groups = [], 
    isTeamLeader 
}) => {
    const [anchorEl, setAnchorEl] = useState(null);

    // Split the full name into parts
    const nameParts = member.name ? member.name.split(' ') : ['', ''];
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ');

    // Get initials from the name
    const getInitials = () => {
        return nameParts.map(part => part[0]).join('').toUpperCase();
    };

    // Get the dominant style color
    const getStyleColor = (style) => {
        const colors = {
            'Visual': '#FF6B6B',
            'Auditory': '#4ECDC4',
            'Kinesthetic': '#45B7D1'
        };
        return colors[style] || '#9E9E9E';
    };

    // Add this function inside MemberCard component
    const getDominantStyle = (learningStyle) => {
        if (!learningStyle) return null;
        
        const scores = {
            visual: parseFloat(learningStyle.visual) || 0,
            auditory: parseFloat(learningStyle.auditory) || 0,
            kinesthetic: parseFloat(learningStyle.kinesthetic) || 0
        };
        
        const total = scores.visual + scores.auditory + scores.kinesthetic;
        if (total === 0) return null;

        const maxScore = Math.max(...Object.values(scores));
        const dominantStyle = Object.entries(scores)
            .find(([_, score]) => score === maxScore)?.[0];

        return dominantStyle ? dominantStyle.charAt(0).toUpperCase() + dominantStyle.slice(1) : null;
    };

    console.log('MemberCard props:', {
        memberName: member.name,
        memberGroups: member.groups
    });

    // Add debug logging for groups
    console.log('Member groups:', {
        memberName: `${member.first_name} ${member.last_name}`,
        groups: member.groups,
        allGroups: groups
    });

    const renderGroupChips = () => {
        if (!member.groups || member.groups.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    No groups
                </Typography>
            );
        }

        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {member.groups.map((group) => (
                    <Chip
                        key={group.id}
                        label={group.name}
                        size="small"
                        onDelete={isTeamLeader ? () => onRemoveFromGroup(member.id, group.id) : undefined}
                    />
                ))}
            </Box>
        );
    };

    return (
        <Paper 
            elevation={3} 
            sx={{ 
                p: { xs: 1.5, sm: 2 },
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                }
            }}
        >
            {/* Header with Avatar and Menu */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                    <Avatar 
                        sx={{ 
                            width: { xs: 48, sm: 56 },
                            height: { xs: 48, sm: 56 },
                            bgcolor: member.learningStyle ? 
                                getStyleColor(getDominantStyle(member.learningStyle)) : 
                                '#9E9E9E'
                        }}
                    >
                        {getInitials()}
                    </Avatar>
                    <Box>
                        <Typography variant="h6" sx={{ 
                            mb: 0.5,
                            fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}>
                            {member.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {member.email}
                        </Typography>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: { xs: 0.5, sm: 1 },
                            mt: 1, 
                            flexWrap: 'wrap' 
                        }}>
                            {member.learningStyle && getDominantStyle(member.learningStyle) && (
                                <Chip
                                    label={getDominantStyle(member.learningStyle)}
                                    size="small"
                                    sx={{
                                        bgcolor: getStyleColor(getDominantStyle(member.learningStyle)),
                                        color: 'white',
                                        fontWeight: 'medium',
                                        '& .MuiChip-label': {
                                            px: 1
                                        }
                                    }}
                                    icon={<SchoolIcon sx={{ color: 'white !important' }} />}
                                />
                            )}
                            {renderGroupChips()}
                        </Box>
                    </Box>
                </Box>
                <IconButton 
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{ p: { xs: 0.5, sm: 1 } }}
                >
                    <MoreVertIcon />
                </IconButton>
            </Box>

            {/* Learning Style Section */}
            <Box sx={{ my: { xs: 1.5, sm: 2 }, flexGrow: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                    Learning Style Profile:
                </Typography>
                {member.learningStyle ? (
                    <Stack spacing={1.5}>
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2">Visual</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {Math.round(member.learningStyle.visual)}%
                                </Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={member.learningStyle.visual}
                                sx={{ 
                                    height: 6, 
                                    borderRadius: 3,
                                    bgcolor: 'rgba(255,107,107,0.2)',
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: '#FF6B6B'
                                    }
                                }}
                            />
                        </Box>
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2">Auditory</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {Math.round(member.learningStyle.auditory)}%
                                </Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={member.learningStyle.auditory}
                                sx={{ 
                                    height: 6, 
                                    borderRadius: 3,
                                    bgcolor: 'rgba(78,205,196,0.2)',
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: '#4ECDC4'
                                    }
                                }}
                            />
                        </Box>
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="body2">Kinesthetic</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {Math.round(member.learningStyle.kinesthetic)}%
                                </Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={member.learningStyle.kinesthetic}
                                sx={{ 
                                    height: 6, 
                                    borderRadius: 3,
                                    bgcolor: 'rgba(69,183,209,0.2)',
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: '#45B7D1'
                                    }
                                }}
                            />
                        </Box>
                    </Stack>
                ) : (
                    <Typography color="text.secondary">
                        No learning style data available
                    </Typography>
                )}
            </Box>

            {/* Add group information */}
            <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
                <Typography variant="subtitle2" color="textSecondary">
                    Groups:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {member.groups?.map((group) => (
                        <Chip
                            key={group.id}
                            label={group.name}
                            size="small"
                            icon={<GroupIcon />}
                            sx={{ mr: 0.5, mb: 0.5 }}
                        />
                    ))}
                </Box>
            </Box>

            {/* Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                <MenuItem onClick={() => {
                    onViewDetails(member);
                    setAnchorEl(null);
                }}>
                    <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
                    View Details
                </MenuItem>
                {isTeamLeader && (
                    <MenuItem 
                        onClick={() => {
                            onDelete(member.id);
                            setAnchorEl(null);
                        }}
                        sx={{ color: 'error.main' }}
                    >
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        Remove Member
                    </MenuItem>
                )}
            </Menu>
        </Paper>
    );
};

export default MemberCard; 